/**
 * Pure questionnaire flow for the landing-page dialog.
 * Discrete steps with named transitions — co-located (webflow-only, not shared with mobile).
 */

export const EQuestionnairePath = {
	FUND: "fund",
	PAYMENTS: "payments",
	CASH_FLOW: "cashFlow",
} as const;

export type TQuestionnairePath = (typeof EQuestionnairePath)[keyof typeof EQuestionnairePath];

export const EQuestionnaireEvent = {
	OPEN: "OPEN",
	CLOSE: "CLOSE",
	SELECT_OPTION: "SELECT_OPTION",
	TOGGLE_OPTION: "TOGGLE_OPTION",
	CONTINUE: "CONTINUE",
} as const;

export type TQuestionnaireEventType = (typeof EQuestionnaireEvent)[keyof typeof EQuestionnaireEvent];

export const EStepSelection = {
	SINGLE: "single",
	MULTI: "multi",
	COMPLETE: "complete",
} as const;

export type TStepSelection = (typeof EStepSelection)[keyof typeof EStepSelection];

export type TCtaHrefKey = "getFunding" | "getStarted" | "manageCashFlow";

export type TQuestionnaireOptionIcon =
	| "purchaseOrders"
	| "unpaidInvoices"
	| "supplierBills"
	| "other"
	| "fund"
	| "payments"
	| "cashFlow";

export interface IQuestionnaireOption {
	id: string;
	label: string;
	/** Optional glyph — picker journey cards and fund-type / funding-use steps. */
	icon?: TQuestionnaireOptionIcon;
}

export interface IQuestionnaireStepDef {
	id: string;
	selection: TStepSelection;
	intro?: string;
	question: string;
	hint?: string;
	options: IQuestionnaireOption[];
	/** Shown on complete step */
	ctaLabel?: string;
	ctaHrefKey?: TCtaHrefKey;
	/** Complete-step body copy; may use {answers} later */
	summary?: string;
}

export interface IQuestionnaireAnswers {
	[stepId: string]: string | string[];
}

export interface IQuestionnaireState {
	open: boolean;
	path: TQuestionnairePath | null;
	stepIndex: number;
	answers: IQuestionnaireAnswers;
	/** In-progress multi-select for the current step */
	pendingMulti: string[];
}

export type TQuestionnaireEvent =
	| { type: typeof EQuestionnaireEvent.OPEN; path: TQuestionnairePath }
	| { type: typeof EQuestionnaireEvent.CLOSE }
	| { type: typeof EQuestionnaireEvent.SELECT_OPTION; optionId: string; stepIndex?: number }
	| { type: typeof EQuestionnaireEvent.TOGGLE_OPTION; optionId: string }
	| { type: typeof EQuestionnaireEvent.CONTINUE };

export const QUESTIONNAIRE_FLOWS: Record<TQuestionnairePath, IQuestionnaireStepDef[]> = {
	[EQuestionnairePath.FUND]: [
		{
			id: "fundingNeed",
			selection: EStepSelection.SINGLE,
			intro: "Triffin Credit pays the supplier now. You repay as the stock sells — up to six months.",
			question: "What do you need the facility for?",
			options: [
				{ id: "purchase_orders", label: "Stock a purchase order", icon: "purchaseOrders" },
				{ id: "supplier_bills", label: "Pay supplier bills now", icon: "supplierBills" },
				{ id: "unpaid_invoices", label: "Get paid early on invoices", icon: "unpaidInvoices" },
				{ id: "mix", label: "A mix of stock and bills", icon: "other" },
			],
		},
		{
			id: "facilityAmount",
			selection: EStepSelection.SINGLE,
			question: "How much do you need for the next order cycle?",
			hint: "Combined draws start from £5,000. Facilities run from £20,000 to £2m.",
			options: [
				{ id: "lt50k", label: "Under £50k" },
				{ id: "50_100k", label: "£50k - £100k" },
				{ id: "100_200k", label: "£100k - £200k" },
				{ id: "200_500k", label: "£200k - £500k" },
				{ id: "500k_1m", label: "£500k - £1m" },
				{ id: "1m_plus", label: "£1m+" },
			],
		},
		{
			id: "repayWindow",
			selection: EStepSelection.SINGLE,
			question: "When does that stock turn back into cash?",
			options: [
				{ id: "within_30", label: "Within 30 days" },
				{ id: "one_to_three", label: "1 - 3 months" },
				{ id: "three_to_six", label: "3 - 6 months" },
				{ id: "not_sure", label: "Not sure yet" },
			],
		},
		{
			id: "annualRevenue",
			selection: EStepSelection.SINGLE,
			question: "Roughly how much revenue does the business make a year?",
			options: [
				{ id: "lt250k", label: "Under £250k" },
				{ id: "250_500k", label: "£250k - £500k" },
				{ id: "500k_1m", label: "£500k - £1m" },
				{ id: "1_5m", label: "£1m - £5m" },
				{ id: "5_15m", label: "£5m - £15m" },
				{ id: "15m_plus", label: "£15m+" },
			],
		},
		{
			id: "complete",
			selection: EStepSelection.COMPLETE,
			intro: "Triffin Credit can cover that next buy, then you repay as it sells.",
			question: "See what you could be eligible for",
			summary:
				"A revolving facility for inventory, supplier bills, and getting paid early on invoices you are already owed.",
			ctaLabel: "Get funding",
			ctaHrefKey: "getFunding",
			options: [],
		},
	],
	[EQuestionnairePath.PAYMENTS]: [
		{
			id: "payToday",
			selection: EStepSelection.SINGLE,
			intro: "Triffin turns the bills in your accounts into one pay run — and can fund it if cash is tight.",
			question: "How do you pay suppliers today?",
			options: [
				{ id: "one_by_one", label: "One bank transfer at a time" },
				{ id: "accounting_run", label: "A payment run in our accounts" },
				{ id: "bank_portal", label: "A bank portal or finance tool" },
				{ id: "depends", label: "It depends on the supplier" },
			],
		},
		{
			id: "payRunPain",
			selection: EStepSelection.SINGLE,
			question: "What slows the pay run down?",
			options: [
				{ id: "admin", label: "Too much manual admin" },
				{ id: "cash_tight", label: "We pay late because cash is tight" },
				{ id: "priority", label: "We can't see who to pay first" },
				{ id: "errors", label: "Errors and chasing remittances" },
			],
		},
		{
			id: "paymentVolume",
			selection: EStepSelection.SINGLE,
			question: "What do you pay suppliers in a typical month?",
			options: [
				{ id: "lt50k", label: "Under £50k" },
				{ id: "50_100k", label: "£50k - £100k" },
				{ id: "100_200k", label: "£100k - £200k" },
				{ id: "200_500k", label: "£200k - £500k" },
				{ id: "500k_plus", label: "£500k+" },
			],
		},
		{
			id: "complete",
			selection: EStepSelection.COMPLETE,
			intro: "You can build that pay run from the bills you already have.",
			question: "Pay suppliers in one click",
			summary: "Connect your accounts, choose who to pay, and send the run — from cash, or from Triffin Credit.",
			ctaLabel: "Get started",
			ctaHrefKey: "getStarted",
			options: [],
		},
	],
	[EQuestionnairePath.CASH_FLOW]: [
		{
			id: "cashPicture",
			selection: EStepSelection.SINGLE,
			intro: "Triffin puts cash, bills, and the next 13 weeks in one place — then tells you who to pay.",
			question: "Where does the cash picture live today?",
			options: [
				{ id: "spreadsheets", label: "Spreadsheets" },
				{ id: "bank_balance", label: "The bank balance" },
				{ id: "accounting", label: "Accounting software" },
				{ id: "no_view", label: "We don't have one view" },
			],
		},
		{
			id: "cashDecision",
			selection: EStepSelection.SINGLE,
			question: "Which decision is hardest right now?",
			options: [
				{ id: "stock_buy", label: "Whether we can afford the next stock buy" },
				{ id: "which_bills", label: "Which bills to pay this week" },
				{ id: "marketing", label: "How much to put into marketing" },
				{ id: "surprises", label: "We get surprised by the balance" },
			],
		},
		{
			id: "connectFirst",
			selection: EStepSelection.SINGLE,
			question: "What should we connect first?",
			options: [
				{ id: "accounting", label: "Accounting (Xero or QuickBooks)" },
				{ id: "bank", label: "Bank accounts" },
				{ id: "both", label: "Both" },
				{ id: "not_sure", label: "Not sure yet" },
			],
		},
		{
			id: "complete",
			selection: EStepSelection.COMPLETE,
			intro: "Connect those accounts and the next 13 weeks stop being a guess.",
			question: "See cash before you commit it",
			summary: "Forecast cash, line up bills and invoices, and decide who to pay — and when.",
			ctaLabel: "Get started",
			ctaHrefKey: "getStarted",
			options: [],
		},
	],
};

export const initialQuestionnaireState: IQuestionnaireState = {
	open: false,
	path: null,
	stepIndex: 0,
	answers: {},
	pendingMulti: [],
};

export function getStepsForPath(path: TQuestionnairePath | null): IQuestionnaireStepDef[] {
	if (!path) return [];
	return QUESTIONNAIRE_FLOWS[path];
}

export function getCurrentStep(state: IQuestionnaireState): IQuestionnaireStepDef | null {
	const steps = getStepsForPath(state.path);
	return steps[state.stepIndex] ?? null;
}

/** Changes when the live turn or any kept answer changes — used to release UI locks. */
export function questionnaireTurnKey(state: IQuestionnaireState): string {
	if (!state.path) return "";
	const answersKey = Object.keys(state.answers)
		.sort()
		.map((stepId) => {
			const value = state.answers[stepId];
			return `${stepId}:${Array.isArray(value) ? value.join(",") : value}`;
		})
		.join("|");
	return `${state.path}:${state.stepIndex}:${answersKey}`;
}

export function getProgress(state: IQuestionnaireState): number {
	const steps = getStepsForPath(state.path);
	if (steps.length === 0) return 0;
	// Progress fills as steps complete; complete step = 100%
	return Math.min(1, (state.stepIndex + 1) / steps.length);
}

export function resolveAnswerLabels(step: IQuestionnaireStepDef, value: string | string[]): string[] {
	const ids = Array.isArray(value) ? value : [value];
	return ids.map((id) => step.options.find((option) => option.id === id)?.label ?? id);
}

export const JOURNEY_STEP_ID = "journey";
export const JOURNEY_STEP_INDEX = -1;

export const QUESTIONNAIRE_JOURNEY_QUESTION = "What can I help you with today?";

export const JOURNEY_OPTIONS: IQuestionnaireOption[] = [
	{ id: EQuestionnairePath.FUND, label: "Get funding", icon: "fund" },
	{ id: EQuestionnairePath.PAYMENTS, label: "Streamline payments with AI", icon: "payments" },
	{ id: EQuestionnairePath.CASH_FLOW, label: "Optimise cash flow", icon: "cashFlow" },
];

export const JOURNEY_PATH_IDS = [
	EQuestionnairePath.FUND,
	EQuestionnairePath.PAYMENTS,
	EQuestionnairePath.CASH_FLOW,
] as const;

export function isJourneyPathId(id: string): id is TQuestionnairePath {
	return (JOURNEY_PATH_IDS as readonly string[]).includes(id);
}

export type TTranscriptAgentEntry = {
	kind: "agent";
	stepId: string;
	intro?: string;
	question: string;
	hint?: string;
	summary?: string;
	isComplete: boolean;
	isCurrent: boolean;
};

export type TTranscriptOptionsEntry = {
	kind: "options";
	stepId: string;
	stepIndex: number;
	question: string;
	options: IQuestionnaireOption[];
	selectedIds: string[];
	isCurrent: boolean;
	selection: TStepSelection;
};

export type TTranscriptEntry = TTranscriptAgentEntry | TTranscriptOptionsEntry;

export type TTranscriptTurn =
	| { kind: "qa"; stepId: string; agent: TTranscriptAgentEntry; options: TTranscriptOptionsEntry }
	| { kind: "complete"; stepId: string; agent: TTranscriptAgentEntry };

/** Pair each agent bubble with its option grid so the UI can render one left/right row. */
export function groupTranscriptTurns(entries: TTranscriptEntry[]): TTranscriptTurn[] {
	const turns: TTranscriptTurn[] = [];
	for (let index = 0; index < entries.length; index++) {
		const entry = entries[index];
		if (!entry || entry.kind !== "agent") continue;
		if (entry.isComplete) {
			turns.push({ kind: "complete", stepId: entry.stepId, agent: entry });
			continue;
		}
		const next = entries[index + 1];
		if (next?.kind === "options" && next.stepId === entry.stepId) {
			turns.push({ kind: "qa", stepId: entry.stepId, agent: entry, options: next });
			index += 1;
		}
	}
	return turns;
}

function selectedIdsForStep(state: IQuestionnaireState, step: IQuestionnaireStepDef, index: number): string[] {
	const answer = state.answers[step.id];
	if (answer !== undefined) {
		return Array.isArray(answer) ? answer : [answer];
	}
	if (index === state.stepIndex && step.selection === EStepSelection.MULTI) {
		return state.pendingMulti;
	}
	return [];
}

function pushStepTranscript(
	entries: TTranscriptEntry[],
	state: IQuestionnaireState,
	step: IQuestionnaireStepDef,
	index: number,
): void {
	const isCurrent = index === state.stepIndex;
	entries.push({
		kind: "agent",
		stepId: step.id,
		intro: step.intro,
		question: step.question,
		hint: step.hint,
		summary: step.summary,
		isComplete: step.selection === EStepSelection.COMPLETE,
		isCurrent,
	});
	if (step.selection === EStepSelection.COMPLETE) return;
	entries.push({
		kind: "options",
		stepId: step.id,
		stepIndex: index,
		question: step.question,
		options: step.options,
		selectedIds: selectedIdsForStep(state, step, index),
		isCurrent,
		selection: step.selection,
	});
}

function pushJourneyTranscript(entries: TTranscriptEntry[], path: TQuestionnairePath): void {
	entries.push({
		kind: "agent",
		stepId: JOURNEY_STEP_ID,
		question: QUESTIONNAIRE_JOURNEY_QUESTION,
		isComplete: false,
		isCurrent: false,
	});
	entries.push({
		kind: "options",
		stepId: JOURNEY_STEP_ID,
		stepIndex: JOURNEY_STEP_INDEX,
		question: QUESTIONNAIRE_JOURNEY_QUESTION,
		options: JOURNEY_OPTIONS,
		selectedIds: [path],
		isCurrent: false,
		selection: EStepSelection.SINGLE,
	});
}

/**
 * Derives a scrollable chat transcript from answers + current step.
 * Journey Q + three cards stay pinned; every answered step and the live step
 * keep their agent bubble and choice grid.
 */
export function buildTranscript(state: IQuestionnaireState): TTranscriptEntry[] {
	const steps = getStepsForPath(state.path);
	if (!state.path || steps.length === 0) return [];

	const entries: TTranscriptEntry[] = [];
	pushJourneyTranscript(entries, state.path);

	for (let index = 0; index <= state.stepIndex; index++) {
		const step = steps[index];
		if (!step) continue;
		pushStepTranscript(entries, state, step, index);
	}

	return entries;
}

function applySingleAnswer(state: IQuestionnaireState, optionId: string, stepIndex: number): IQuestionnaireState {
	const steps = getStepsForPath(state.path);
	const step = steps[stepIndex];
	if (!step || step.selection !== EStepSelection.SINGLE) return state;
	if (stepIndex < 0 || stepIndex > state.stepIndex) return state;

	const previous = state.answers[step.id];
	if (stepIndex < state.stepIndex && previous === optionId) {
		return state;
	}

	const answers: IQuestionnaireAnswers = {};
	for (let index = 0; index < stepIndex; index++) {
		const prior = steps[index];
		if (!prior) continue;
		const value = state.answers[prior.id];
		if (value !== undefined) {
			answers[prior.id] = value;
		}
	}
	answers[step.id] = optionId;

	return {
		...state,
		answers,
		pendingMulti: [],
		stepIndex: Math.min(stepIndex + 1, Math.max(steps.length - 1, 0)),
	};
}

function advanceAfterAnswer(state: IQuestionnaireState, stepId: string, value: string | string[]): IQuestionnaireState {
	const steps = getStepsForPath(state.path);
	const nextIndex = state.stepIndex + 1;
	return {
		...state,
		answers: { ...state.answers, [stepId]: value },
		pendingMulti: [],
		stepIndex: Math.min(nextIndex, Math.max(steps.length - 1, 0)),
	};
}

export function questionnaireReducer(state: IQuestionnaireState, event: TQuestionnaireEvent): IQuestionnaireState {
	switch (event.type) {
		case EQuestionnaireEvent.OPEN:
			return {
				open: true,
				path: event.path,
				stepIndex: 0,
				answers: {},
				pendingMulti: [],
			};
		case EQuestionnaireEvent.CLOSE:
			return {
				...initialQuestionnaireState,
			};
		case EQuestionnaireEvent.SELECT_OPTION: {
			if (!state.open || !state.path) return state;
			const stepIndex = event.stepIndex ?? state.stepIndex;
			return applySingleAnswer(state, event.optionId, stepIndex);
		}
		case EQuestionnaireEvent.TOGGLE_OPTION: {
			if (!state.open || !state.path) return state;
			const step = getCurrentStep(state);
			if (!step || step.selection !== EStepSelection.MULTI) return state;
			const exists = state.pendingMulti.includes(event.optionId);
			return {
				...state,
				pendingMulti: exists
					? state.pendingMulti.filter((id) => id !== event.optionId)
					: [...state.pendingMulti, event.optionId],
			};
		}
		case EQuestionnaireEvent.CONTINUE: {
			if (!state.open || !state.path) return state;
			const step = getCurrentStep(state);
			if (!step || step.selection !== EStepSelection.MULTI) return state;
			if (state.pendingMulti.length === 0) return state;
			return advanceAfterAnswer(state, step.id, state.pendingMulti);
		}
		default:
			return state;
	}
}
